import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import { toast } from 'sonner';
import { Package, Truck, Factory, Clock, CheckCircle, MapPin, ChevronRight, Search, User, Mail, Lock, Ship, Anchor, Box, Download, CreditCard, Upload, FileText, AlertCircle, Settings, Send, AlertTriangle, Trash2, TrendingUp, DollarSign, PiggyBank, BarChart3, Share2, Copy, Twitter, Facebook, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SquarePaymentForm from '../components/SquarePaymentForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Order status progression
const STATUS_STEPS = [
  { key: 'filling', label: 'Filling', icon: Package, description: 'Container is being filled with orders' },
  { key: 'po_sent', label: 'PO Sent', icon: Send, description: 'Production order sent to factory' },
  { key: 'in_production', label: 'In Production', icon: Factory, description: 'Materials being manufactured' },
  { key: 'production_complete', label: 'Production Complete', icon: CheckCircle, description: 'Ready to ship — balance due' },
  { key: 'at_port', label: 'At Port', icon: Anchor, description: 'Container at port for loading' },
  { key: 'on_vessel', label: 'On Vessel', icon: Ship, description: 'Shipping across the ocean' },
  { key: 'arrived', label: 'Arrived', icon: Box, description: 'At destination port' },
  { key: 'unloaded', label: 'Unloaded', icon: Anchor, description: 'Unloaded from vessel' },
  { key: 'customs_cleared', label: 'Customs Cleared', icon: CheckCircle, description: 'Released from customs' },
  { key: 'on_rail', label: 'On Rail', icon: Truck, description: 'On rail to nearest hub' },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, description: 'In transit to final destination' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Delivered to your address' }
];

// Statuses where balance can be paid
const BALANCE_DUE_STATUSES = ['in_production', 'at_port', 'on_vessel', 'arrived', 'unloaded', 'customs_cleared', 'on_rail', 'out_for_delivery'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

function MyOrders() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingGoogleAuth, setProcessingGoogleAuth] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [payingBalance, setPayingBalance] = useState(null); // pledge_id of order being paid
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupOrderId, setLookupOrderId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [uploadingTaxDoc, setUploadingTaxDoc] = useState(null);
  const [portalTab, setPortalTab] = useState('orders');
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', street: '', city: '', state: '', zip_code: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [balancePaymentModal, setBalancePaymentModal] = useState(null); // { pledge_id, email, total_due, balance_amount, etc }
  const [balanceOtpSent, setBalanceOtpSent] = useState(false);
  const [balanceOtpCode, setBalanceOtpCode] = useState('');
  const [balanceOtpVerified, setBalanceOtpVerified] = useState(false);
  const [balanceVerificationToken, setBalanceVerificationToken] = useState(null);
  const [sendingBalanceOtp, setSendingBalanceOtp] = useState(false);
  const [verifyingBalanceOtp, setVerifyingBalanceOtp] = useState(false);

  // Process Google OAuth callback if session_id is in URL hash
  useEffect(() => {
    const processGoogleAuth = async () => {
      const hash = window.location.hash;
      
      // Check if this is a Google OAuth callback
      if (!hash || !hash.includes('session_id=')) {
        return false;
      }
      
      setProcessingGoogleAuth(true);
      setLoading(true);
      
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      
      if (!sessionId) {
        setProcessingGoogleAuth(false);
        return false;
      }
      
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId }
          }
        );
        
        if (response.data.user) {
          // Store in localStorage for persistence
          localStorage.setItem('bluecrate_user', JSON.stringify(response.data.user));
          if (response.data.session_token) {
            localStorage.setItem('bluecrate_session_token', response.data.session_token);
          }
          
          // Set user state
          setUser(response.data.user);
          toast.success(`Welcome, ${response.data.user.name || response.data.user.email}!`);
          
          // Fetch orders
          fetchOrders(response.data.user.email, response.data.session_token);
        }
        
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        setProcessingGoogleAuth(false);
        return true;
        
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Google sign-in failed: ${errorMsg}`);
        window.history.replaceState(null, '', window.location.pathname);
        setProcessingGoogleAuth(false);
        setLoading(false);
        return false;
      }
    };
    
    // Run Google auth check first
    processGoogleAuth().then((wasGoogleAuth) => {
      // If not a Google auth callback, check regular auth
      if (!wasGoogleAuth) {
        checkAuth();
      }
    });
  }, []); // Run only once on mount

  // Populate profile form when user data loads
  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      try {
        const storedToken = localStorage.getItem('bluecrate_session_token');
        const config = storedToken
          ? { headers: { 'Authorization': `Bearer ${storedToken}` } }
          : {};
        const res = await axios.get(`${BACKEND_URL}/api/me`, config);
        const addr = res.data.saved_address || {};
        setProfileForm({
          name: res.data.name || user.name || '',
          phone: res.data.phone || '',
          street: addr.street || '',
          city: addr.city || '',
          state: addr.state || '',
          zip_code: addr.zip_code || ''
        });
      } catch {
        setProfileForm(prev => ({ ...prev, name: user.name || '' }));
      }
    };
    loadProfile();
  }, [user]);


  const checkAuth = async () => {
    // Use localStorage tokens only (withCredentials won't work cross-domain)
    const storedUser = localStorage.getItem('bluecrate_user');
    const storedToken = localStorage.getItem('bluecrate_session_token');
    
    if (storedUser && storedToken) {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        if (res.data?.email) {
          setUser(res.data);
          fetchOrders(res.data.email, storedToken);
          return;
        }
      } catch {
        localStorage.removeItem('bluecrate_user');
        localStorage.removeItem('bluecrate_session_token');
      }
    }
    setLoading(false);
  };

  const fetchOrders = async (email, token = null) => {
    try {
      const storedToken = token || localStorage.getItem('bluecrate_session_token');
      const config = storedToken
        ? { headers: { 'Authorization': `Bearer ${storedToken}` } }
        : { params: { email } };
      const res = await axios.get(`${BACKEND_URL}/api/my-orders`, config);
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    setLoadingDashboard(true);
    try {
      const storedToken = localStorage.getItem('bluecrate_session_token');
      const config = storedToken
        ? { headers: { 'Authorization': `Bearer ${storedToken}` } }
        : {};
      const res = await axios.get(`${BACKEND_URL}/api/me/dashboard`, config);
      setDashboardStats(res.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Fetch dashboard stats when user is logged in and switches to dashboard tab
  useEffect(() => {
    if (user && portalTab === 'dashboard' && !dashboardStats) {
      fetchDashboardStats();
    }
  }, [user, portalTab, dashboardStats]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const res = await axios.post(`${BACKEND_URL}${endpoint}`, authForm);
      
      // Store in localStorage for cross-domain support
      if (res.data.session_token) {
        localStorage.setItem('bluecrate_session_token', res.data.session_token);
      }
      localStorage.setItem('bluecrate_user', JSON.stringify(res.data.user));
      
      toast.success(res.data.message);
      setUser(res.data.user);
      setShowAuth(false);
      fetchOrders(res.data.user.email, res.data.session_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupEmail || !lookupOrderId) {
      toast.error('Please enter both email and order ID');
      return;
    }

    try {
      const res = await axios.get(`${BACKEND_URL}/api/orders/lookup/${lookupOrderId}`, {
        params: { email: lookupEmail }
      });
      setLookupResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order not found');
      setLookupResult(null);
    }
  };

  const handleDownloadInvoice = async (pledgeId, email) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders/${pledgeId}/invoice`, {
        params: { email },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BlueCrate_Invoice_${pledgeId.slice(-8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: forgotEmail
      });
      setForgotSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handlePayBalance = async (order) => {
    setPayingBalance(order.pledge_id);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/orders/${order.pledge_id}/pay-balance`, {
        email: order.user_email || order.customer_email || order.email,
        origin_url: window.location.origin
      });
      
      // Open Square payment modal
      if (response.data.requires_square_payment) {
        setBalancePaymentModal({
          ...response.data,
          order: order
        });
        setOtpVerified(false);
        setVerificationToken(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to prepare payment');
    } finally {
      setPayingBalance(null);
    }
  };

  const handleBalancePaymentSuccess = async (paymentData) => {
    if (!balancePaymentModal) return;
    
    try {
      // Complete the balance payment on the backend
      const response = await axios.post(
        `${BACKEND_URL}/api/orders/${balancePaymentModal.pledge_id}/complete-balance-payment`,
        {
          email: balancePaymentModal.email,
          square_payment_id: paymentData.payment_id,
          amount_paid: balancePaymentModal.total_due
        }
      );
      
      if (response.data.success) {
        toast.success('Balance paid successfully!');
        setBalancePaymentModal(null);
        setBalanceOtpSent(false);
        setBalanceOtpCode('');
        setBalanceOtpVerified(false);
        setBalanceVerificationToken(null);
        // Refresh orders
        const storedToken = localStorage.getItem('bluecrate_session_token');
        fetchOrders(user?.email, storedToken);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete payment');
    }
  };

  const handleSendBalanceOtp = async () => {
    if (!balancePaymentModal?.email) return;
    setSendingBalanceOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/otp/send`, { email: balancePaymentModal.email.toLowerCase().trim() });
      setBalanceOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingBalanceOtp(false);
    }
  };

  const handleVerifyBalanceOtp = async () => {
    if (!balanceOtpCode || balanceOtpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifyingBalanceOtp(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/otp/verify`, { 
        email: balancePaymentModal.email.toLowerCase().trim(), 
        otp_code: balanceOtpCode 
      });
      setBalanceVerificationToken(res.data.verification_token);
      setBalanceOtpVerified(true);
      toast.success('Email verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingBalanceOtp(false);
    }
  };

  const handleTaxExemptUpload = async (order, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PDF, JPG, or PNG file');
      return;
    }
    setUploadingTaxDoc(order.pledge_id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', order.user_email);
      await axios.post(`${BACKEND_URL}/api/orders/${order.pledge_id}/tax-exempt-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Tax exempt document uploaded! We will review within 24-48 hours.');
      // Optimistic update so UI immediately reflects uploaded state
      setOrders(prev => prev.map(o =>
        o.pledge_id === order.pledge_id
          ? { ...o, tax_exempt_doc_uploaded: true, tax_exempt_doc_filename: file.name, tax_exempt_status: 'pending_review' }
          : o
      ));
      // Refresh orders with correct email and token
      const storedToken = localStorage.getItem('bluecrate_session_token');
      fetchOrders(user?.email || order.user_email, storedToken);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingTaxDoc(null);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${BACKEND_URL}/api/me/profile`, {
        name: profileForm.name,
        phone: profileForm.phone,
        saved_address: {
          street: profileForm.street,
          city: profileForm.city,
          state: profileForm.state,
          zip_code: profileForm.zip_code
        }
      }, getAuthConfig());
      
      toast.success('Profile updated successfully!');
      // Update local user state
      setUser(prev => ({ ...prev, name: profileForm.name }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const getStatusIcon = (status) => {
    const step = STATUS_STEPS.find(s => s.key === status);
    if (step) {
      const Icon = step.icon;
      return <Icon className="w-5 h-5" />;
    }
    switch (status) {
      case 'shipped': return <Truck className="w-5 h-5 text-purple-600" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      default: return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusText = (status) => {
    const step = STATUS_STEPS.find(s => s.key === status);
    if (step) return step.label;
    switch (status) {
      case 'shipped': return 'Shipped';
      case 'completed': return 'Completed';
      case 'active': return 'Awaiting Orders';
      default: return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'production_initiated':
      case 'in_production': return 'bg-green-100 text-green-800 border-green-200';
      case 'filling': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'at_port':
      case 'on_vessel': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'arrived':
      case 'unloaded': return 'bg-sky-100 text-sky-800 border-sky-200';
      case 'customs_cleared': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'on_rail': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'out_for_delivery': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'delivered':
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getStatusIndex = (status) => {
    return STATUS_STEPS.findIndex(s => s.key === status);
  };

  const renderOrderCard = (order) => {
    const depositPaid = parseFloat(order.deposit_paid || 0);
    const totalPrice = parseFloat(order.total_price || 0);
    const balanceDue = totalPrice - depositPaid;
    const address = order.shipping_address || {};
    const currentStatus = (() => {
      const raw = order.order_status || order.display_status || 'filling';
      // Normalize container-level "active" to the timeline step "filling"
      return raw === 'active' ? 'filling' : raw;
    })();
    const currentStatusIndex = getStatusIndex(currentStatus);

    return (
      <div key={order.pledge_id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow" data-testid={`order-card-${order.pledge_id}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 border-b">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600">Order ID</p>
              <p className="font-bold text-lg" data-testid="order-id">#{order.pledge_id?.slice(-8)}</p>
              {order.container_info?.container_number && (
                <p className="text-xs text-gray-500">Container: #{order.container_info.container_number}</p>
              )}
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusColor(currentStatus)}`}>
              {getStatusIcon(currentStatus)}
              <span className="text-sm font-semibold">{getStatusText(currentStatus)}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Tracking & Container Info */}
          {(order.container_info?.container_number || order.container_info?.carrier_tracking_number) && (
            <div className="mb-4 p-4 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-white" data-testid={`tracking-block-${order.pledge_id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {order.container_info?.container_number && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">BlueCrate Container #</p>
                    <p className="font-mono text-sm font-bold text-gray-900" data-testid="bluecrate-container-number">{order.container_info.container_number}</p>
                    <p className="text-[11px] text-gray-500 mt-1">Internal reference</p>
                  </div>
                )}
                {order.container_info?.carrier_tracking_number && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                      {order.container_info.carrier ? `${order.container_info.carrier} Tracking #` : 'Carrier Tracking #'}
                    </p>
                    <p className="font-mono text-sm font-bold text-primary" data-testid="carrier-tracking-number">{order.container_info.carrier_tracking_number}</p>
                    {order.container_info.carrier_tracking_url ? (
                      <a
                        href={order.container_info.carrier_tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-primary hover:underline"
                        data-testid="carrier-tracking-link"
                      >
                        Track on {order.container_info.carrier || 'carrier site'} <ChevronRight className="w-3 h-3" />
                      </a>
                    ) : (
                      <p className="text-[11px] text-gray-500 mt-1">Search on your carrier's site</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Product Info */}
          <div className="mb-4">
            <h3 className="font-bold text-lg">{order.product_name}</h3>
            <p className="text-gray-600">{parseInt(order.sqft || 0).toLocaleString()} sqft @ ${parseFloat(order.price_per_sqft || 0).toFixed(2)}/sqft</p>
          </div>

          {/* Status Timeline */}
          <div className="mb-4 p-4 bg-gray-50 rounded-xl">
            <h4 className="text-sm font-semibold mb-3">Order Progress</h4>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              <div 
                className="absolute left-4 top-0 w-0.5 bg-primary transition-all"
                style={{ height: `${Math.max(0, (currentStatusIndex / (STATUS_STEPS.length - 1)) * 100)}%` }}
              ></div>
              
              {/* Status Steps */}
              <div className="space-y-3">
                {STATUS_STEPS.map((step, index) => {
                  const isCompleted = index < currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const Icon = step.icon;

                  // Find the most recent timestamp for this step from status_history
                  const history = order.status_history || [];
                  const historyEntry = [...history].reverse().find(h => h.status === step.key);
                  const ts = historyEntry?.timestamp ? new Date(historyEntry.timestamp) : null;
                  const tsLabel = ts && !isNaN(ts) ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

                  return (
                    <div key={step.key} className={`flex items-center gap-3 relative rounded-lg px-1 py-1 transition-colors ${
                      isCurrent ? 'bg-primary/10' : ''
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all ${
                        isCurrent
                          ? 'bg-primary text-white ring-4 ring-primary/30 scale-110'
                          : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-400'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className={`text-sm font-semibold ${
                            isCurrent ? 'text-primary' : isCompleted ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </p>
                          {tsLabel && (
                            <p className="text-xs text-gray-500" data-testid={`status-timestamp-${step.key}`}>{tsLabel}</p>
                          )}
                        </div>
                        {isCurrent && (
                          <p className="text-xs text-primary/80 font-medium">{step.description}</p>
                        )}
                        {historyEntry?.notes && (isCompleted || isCurrent) && (
                          <p className="text-xs text-gray-500 italic mt-0.5">{historyEntry.notes}</p>
                        )}
                      </div>
                      {isCompleted && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      {isCurrent && (
                        <span className="px-2 py-0.5 bg-primary text-white text-xs font-bold rounded-full">Current</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Container Progress (if still filling) */}
          {order.container_info && currentStatusIndex <= 0 && (
            <div className="mb-4 p-4 bg-blue-50 rounded-xl">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold">Container Fill Progress</span>
                <span className="text-sm font-bold text-primary">{order.container_info.fill_percentage}%</span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    order.container_info.fill_percentage >= 50 ? 'bg-green-500' : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(order.container_info.fill_percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Production starts at 80% capacity
              </p>
            </div>
          )}

          {/* Estimated Delivery */}
          {order.estimated_delivery && (
            <div className="mb-4 p-3 bg-purple-50 rounded-xl flex items-center gap-3">
              <Truck className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-gray-600">Estimated Delivery</p>
                <p className="font-semibold text-purple-800">{order.estimated_delivery}</p>
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <p className="text-xs text-gray-600">Deposit Paid (30%)</p>
              <p className="font-bold text-green-700">${depositPaid.toFixed(2)}</p>
            </div>
            <div className={`p-3 rounded-xl ${order.balance_paid || order.fully_paid ? 'bg-green-50' : 'bg-orange-50'}`}>
              <p className="text-xs text-gray-600">
                {order.balance_paid || order.fully_paid ? 'Balance Paid' : 'Balance Due (70%)'}
              </p>
              <p className={`font-bold ${order.balance_paid || order.fully_paid ? 'text-green-700' : 'text-orange-700'}`}>
                ${balanceDue.toFixed(2)}
                {(order.balance_paid || order.fully_paid) && <CheckCircle className="w-4 h-4 inline ml-1" />}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadInvoice(order.pledge_id, order.user_email)}
              className="flex-1"
              data-testid="download-invoice-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Invoice
            </Button>
            
            {/* Pay Balance Button - show when order is in production or later, and balance not paid */}
            {!order.balance_paid && !order.fully_paid && BALANCE_DUE_STATUSES.includes(currentStatus) && (
              <Button
                onClick={() => handlePayBalance(order)}
                disabled={payingBalance === order.pledge_id}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                data-testid="pay-balance-btn"
              >
                {payingBalance === order.pledge_id ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Pay Balance
              </Button>
            )}
            
            {/* Fully Paid Badge */}
            {(order.balance_paid || order.fully_paid) && (
              <div className="flex-1 flex items-center justify-center bg-green-100 text-green-700 rounded-lg py-2 px-4">
                <CheckCircle className="w-4 h-4 mr-2" />
                <span className="font-semibold">Fully Paid</span>
              </div>
            )}
          </div>

          {/* Tax Exempt Document Upload */}
          {order.tax_exempt && (
            <div className="mb-4 p-4 rounded-xl border" data-testid={`tax-exempt-section-${order.pledge_id}`}>
              {order.tax_exempt_status === 'approved' || order.tax_exempt_verified ? (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Tax Exempt - Approved</span>
                </div>
              ) : order.tax_exempt_status === 'rejected' ? (
                <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <div>
                    <span className="font-semibold block">Tax Exempt - Rejected</span>
                    <span className="text-sm">Tax has been added to your balance. Contact us if you believe this is an error.</span>
                  </div>
                </div>
              ) : order.tax_exempt_doc_uploaded ? (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                  <Clock className="w-5 h-5" />
                  <div>
                    <span className="font-semibold block">Tax Exempt - Under Review</span>
                    <span className="text-sm">Your document has been submitted and is being reviewed.</span>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-blue-200 border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Tax Exempt Document Required</span>
                  </div>
                  <p className="text-sm text-blue-700 mb-3">
                    Please upload your tax exemption certificate (PDF, JPG, or PNG) within 72 hours.
                  </p>
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                      uploadingTaxDoc === order.pledge_id
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    data-testid={`upload-tax-doc-btn-${order.pledge_id}`}
                  >
                    {uploadingTaxDoc === order.pledge_id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploadingTaxDoc === order.pledge_id ? 'Uploading...' : 'Upload Document'}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      disabled={uploadingTaxDoc === order.pledge_id}
                      onChange={(e) => handleTaxExemptUpload(order, e.target.files[0])}
                      data-testid={`tax-doc-input-${order.pledge_id}`}
                    />
                  </label>
                  {order.tax_exempt_deadline && (
                    <p className="text-xs text-blue-600 mt-2">
                      Deadline: {new Date(order.tax_exempt_deadline).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Shipping Address */}
          <div className="text-sm text-gray-600">
            <p className="font-semibold mb-1">Shipping To:</p>
            <p>{address.street}, {address.city}, {address.state} {address.zip_code}</p>
          </div>

          {/* Order Date */}
          <div className="mt-4 pt-4 border-t text-xs text-gray-500">
            Ordered: {new Date(order.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-8 pt-28">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="page-title">My Orders</h1>
          <p className="text-gray-600">Track your orders and container progress</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            {processingGoogleAuth && (
              <p className="mt-4 text-gray-600">Signing you in with Google...</p>
            )}
          </div>
        ) : user ? (
          /* Logged In View */
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{user.name || user.email}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, getAuthConfig());
                  // Clear localStorage
                  localStorage.removeItem('bluecrate_user');
                  localStorage.removeItem('bluecrate_session_token');
                  setUser(null);
                  setOrders([]);
                }}
                data-testid="logout-btn"
              >
                Sign Out
              </Button>
            </div>

            {/* Portal Tabs */}
            <div className="flex space-x-2 mb-6 border-b pb-2">
              <button
                onClick={() => setPortalTab('dashboard')}
                className={`flex items-center px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  portalTab === 'dashboard' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="portal-tab-dashboard"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </button>
              <button
                onClick={() => setPortalTab('orders')}
                className={`flex items-center px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  portalTab === 'orders' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="portal-tab-orders"
              >
                <Package className="w-4 h-4 mr-2" />
                My Orders
              </button>
              <button
                onClick={() => setPortalTab('profile')}
                className={`flex items-center px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  portalTab === 'profile' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="portal-tab-profile"
              >
                <Settings className="w-4 h-4 mr-2" />
                My Info
              </button>
            </div>

            {/* Dashboard Tab */}
            {portalTab === 'dashboard' && (
              <div className="space-y-6">
                {loadingDashboard ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : dashboardStats ? (
                  <>
                    {/* Savings Hero */}
                    <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-3xl p-8 text-white relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <PiggyBank className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="text-green-100 text-sm font-medium">Your Lifetime Savings</p>
                            <p className="text-4xl font-bold">${dashboardStats.total_savings?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                        <p className="text-green-100 text-sm">
                          You've saved <span className="font-bold text-white">{dashboardStats.savings_percentage}%</span> compared to traditional retail prices
                        </p>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm">Total Spent</p>
                        <p className="text-2xl font-bold text-gray-900">${dashboardStats.total_spent?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>

                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-red-600" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm">Retail Value</p>
                        <p className="text-2xl font-bold text-gray-900">${dashboardStats.total_retail_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-400">What you would have paid</p>
                      </div>

                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-purple-600" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm">Total Orders</p>
                        <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_orders}</p>
                      </div>

                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Box className="w-5 h-5 text-amber-600" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm">Total Sqft</p>
                        <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_sqft?.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Comparison Breakdown */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        How You're Saving
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">
                              <span className="text-red-800 text-xs font-bold">🏪</span>
                            </div>
                            <span className="font-medium text-red-800">Retail Store Price</span>
                          </div>
                          <span className="font-bold text-red-700">${dashboardStats.total_retail_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                              <span className="text-green-800 text-xs font-bold">📦</span>
                            </div>
                            <span className="font-medium text-green-800">Your BlueCrate Price</span>
                          </div>
                          <span className="font-bold text-green-700">${dashboardStats.total_spent?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-100 to-green-50 rounded-xl border-2 border-green-300">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                              <PiggyBank className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-bold text-green-900">Your Total Savings</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-2xl text-green-700">${dashboardStats.total_savings?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            <p className="text-xs text-green-600">{dashboardStats.savings_percentage}% off retail</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    {dashboardStats.total_orders === 0 && (
                      <div className="text-center py-8 bg-blue-50 rounded-2xl">
                        <Package className="w-12 h-12 mx-auto text-blue-400 mb-3" />
                        <h3 className="font-bold text-lg mb-2">No orders yet</h3>
                        <p className="text-gray-600 mb-4">Start saving today with BlueCrate's wholesale prices!</p>
                        <Link to="/products">
                          <Button className="bg-primary hover:bg-primary/90">
                            Browse Products <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    )}

                    {/* Share Your Savings */}
                    {dashboardStats.total_savings > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div>
                            <h3 className="font-bold text-lg flex items-center gap-2 text-blue-900">
                              <Share2 className="w-5 h-5" />
                              Share Your Savings!
                            </h3>
                            <p className="text-sm text-blue-700 mt-1">Let others know how much you've saved with BlueCrate</p>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              onClick={() => {
                                const text = `I've saved $${dashboardStats.total_savings?.toLocaleString()} (${dashboardStats.savings_percentage}% off retail!) shopping with BlueCrate Group for wholesale flooring. Check them out!`;
                                navigator.clipboard.writeText(text);
                                toast.success('Copied to clipboard!');
                              }}
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-100"
                              data-testid="share-copy-btn"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy
                            </Button>
                            <Button
                              onClick={() => {
                                const text = encodeURIComponent(`I've saved $${dashboardStats.total_savings?.toLocaleString()} (${dashboardStats.savings_percentage}% off retail!) shopping with @BlueCrateGroup for wholesale flooring! 🏠💰`);
                                window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent('https://bluecrategroup.com')}`, '_blank');
                              }}
                              className="bg-black hover:bg-gray-800 text-white"
                              data-testid="share-twitter-btn"
                            >
                              <Twitter className="w-4 h-4 mr-2" />
                              Post
                            </Button>
                            <Button
                              onClick={() => {
                                const text = encodeURIComponent(`I've saved $${dashboardStats.total_savings?.toLocaleString()} shopping with BlueCrate Group for wholesale flooring! Check them out at bluecrategroup.com`);
                                window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid="share-facebook-btn"
                            >
                              <Facebook className="w-4 h-4 mr-2" />
                              Share
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {dashboardStats.user_since && (
                      <p className="text-center text-sm text-gray-500">
                        Member since {new Date(dashboardStats.user_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Unable to load dashboard data</p>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {portalTab === 'orders' && (
              <>
                {orders.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h2 className="text-xl font-bold mb-2">No Orders Yet</h2>
                    <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
                    <Link to="/products">
                      <Button className="bg-primary hover:bg-primary/90">
                        Browse Products <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {orders.map(renderOrderCard)}
                  </div>
                )}
              </>
            )}

            {/* My Info / Profile Tab */}
            {portalTab === 'profile' && (
              <div className="max-w-2xl mx-auto space-y-6">
                {/* Profile Form */}
                <form onSubmit={handleSaveProfile} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Personal Information
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="Your name"
                          data-testid="profile-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={user.email}
                          disabled
                          className="w-full p-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500"
                          data-testid="profile-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="(555) 123-4567"
                        data-testid="profile-phone-input"
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Default Shipping Address
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">This address will auto-fill at checkout.</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        value={profileForm.street}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, street: e.target.value }))}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="123 Main St"
                        data-testid="profile-street-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={profileForm.city}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="City"
                          data-testid="profile-city-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <select
                          value={profileForm.state}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          data-testid="profile-state-select"
                        >
                          <option value="">Select</option>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          value={profileForm.zip_code}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, zip_code: e.target.value }))}
                          className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                          placeholder="12345"
                          maxLength={5}
                          data-testid="profile-zip-input"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={savingProfile}
                    className="w-full mt-6 h-12 rounded-xl bg-primary hover:bg-primary/90"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>

                {/* Tax Exempt Documents Section */}
                {orders.some(o => o.tax_exempt) && (
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100" data-testid="profile-tax-exempt-section">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Tax Exempt Documents
                    </h2>
                    <div className="space-y-3">
                      {orders.filter(o => o.tax_exempt).map(order => (
                        <div key={order.pledge_id} className="p-4 rounded-xl border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-semibold">Order #{order.pledge_id?.slice(-8)}</span>
                              <span className="text-gray-500 text-sm ml-2">- {order.product_name}</span>
                            </div>
                            {order.tax_exempt_status === 'approved' || order.tax_exempt_verified ? (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">Approved</span>
                            ) : order.tax_exempt_status === 'rejected' ? (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">Rejected</span>
                            ) : order.tax_exempt_doc_uploaded ? (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">Under Review</span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">Upload Required</span>
                            )}
                          </div>
                          {!order.tax_exempt_doc_uploaded && !order.tax_exempt_verified && order.tax_exempt_status !== 'rejected' && (
                            <label
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-colors ${
                                uploadingTaxDoc === order.pledge_id
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                              data-testid={`profile-upload-tax-doc-${order.pledge_id}`}
                            >
                              {uploadingTaxDoc === order.pledge_id ? (
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                              {uploadingTaxDoc === order.pledge_id ? 'Uploading...' : 'Upload Certificate'}
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                disabled={uploadingTaxDoc === order.pledge_id}
                                onChange={(e) => handleTaxExemptUpload(order, e.target.files[0])}
                              />
                            </label>
                          )}
                          {order.tax_exempt_doc_uploaded && order.tax_exempt_doc_filename && (
                            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                              <FileText className="w-3 h-3" />
                              {order.tax_exempt_doc_filename}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delete Account */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-red-100">
                  <h2 className="text-lg font-bold text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Danger Zone
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 mb-4">Permanently delete your account. Your order history will be kept for our records but your login will be removed.</p>
                  {!showDeleteAccount ? (
                    <button
                      onClick={() => setShowDeleteAccount(true)}
                      className="px-4 py-2 text-sm border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                      data-testid="show-delete-account-btn"
                    >
                      Delete My Account
                    </button>
                  ) : (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-sm font-semibold text-red-700 mb-3">Are you sure? This action cannot be undone.</p>
                      <div className="flex gap-3">
                        <Button
                          onClick={async () => {
                            setDeletingAccount(true);
                            try {
                              await axios.delete(`${BACKEND_URL}/api/me/account`, getAuthConfig());
                              toast.success('Account deleted. Goodbye!');
                              localStorage.removeItem('session_token');
                              localStorage.removeItem('user');
                              window.location.href = '/';
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to delete account');
                              setDeletingAccount(false);
                            }
                          }}
                          disabled={deletingAccount}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="confirm-delete-account-btn"
                        >
                          {deletingAccount ? 'Deleting...' : 'Yes, Delete My Account'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowDeleteAccount(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Guest View */
          <div className="grid md:grid-cols-2 gap-8">
            {/* Login/Register Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold">{isLogin ? 'Sign In' : 'Create Account'}</h2>
                <p className="text-gray-600 text-sm mt-1">
                  {isLogin ? 'Access your order history and tracking' : 'Track all your orders in one place'}
                </p>
              </div>

              <form onSubmit={handleAuth} className="space-y-4">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={authForm.name}
                        onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                        className="w-full pl-10 p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                        placeholder="Your name"
                        data-testid="register-name"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={authForm.email}
                      onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                      className="w-full pl-10 p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="your.email@example.com"
                      required
                      data-testid="auth-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                      className="w-full pl-10 p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="auth-password"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-primary hover:bg-primary/90"
                  disabled={authLoading}
                  data-testid="auth-submit"
                >
                  {authLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Google Sign In */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-2"
                onClick={() => {
                  // Store current path for redirect after auth
                  sessionStorage.setItem('auth_redirect', '/my-orders');
                  const redirectUrl = window.location.origin + '/my-orders';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                data-testid="google-signin-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="text-center mt-6 space-y-2">
                {isLogin && (
                  <button
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotSent(false);
                      setForgotEmail(authForm.email);
                    }}
                    className="text-gray-500 hover:text-primary hover:underline text-sm block w-full"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:underline text-sm"
                  data-testid="toggle-auth-mode"
                >
                  {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
                </button>
              </div>

              {!isLogin && (
                <p className="text-xs text-center text-gray-500 mt-4">
                  Your past orders will be automatically linked to your account
                </p>
              )}
            </div>

            {/* Order Lookup Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-orange-600" />
                </div>
                <h2 className="text-xl font-bold">Quick Order Lookup</h2>
                <p className="text-gray-600 text-sm mt-1">
                  Check your order status without signing in
                </p>
              </div>

              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Order Email</label>
                  <input
                    type="email"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                    placeholder="Email used for order"
                    data-testid="lookup-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Order ID</label>
                  <input
                    type="text"
                    value={lookupOrderId}
                    onChange={(e) => setLookupOrderId(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                    placeholder="e.g., pledge_abc123"
                    data-testid="lookup-order-id"
                  />
                </div>
                <Button 
                  type="submit" 
                  variant="outline"
                  className="w-full h-12 border-2 border-orange-500 text-orange-600 hover:bg-orange-50"
                  data-testid="lookup-submit"
                >
                  <Search className="w-4 h-4 mr-2" /> Find Order
                </Button>
              </form>

              {/* Lookup Result */}
              {lookupResult && (
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-bold mb-2">Order Found!</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Product:</strong> {lookupResult.order.product_name}</p>
                    <p><strong>Quantity:</strong> {parseInt(lookupResult.order.sqft).toLocaleString()} sqft</p>
                    <p><strong>Status:</strong> {lookupResult.order.status}</p>
                    {lookupResult.container_info && (
                      <div className="mt-3 p-3 bg-white rounded-lg">
                        <p className="font-semibold">Container #{lookupResult.container_info.container_number}</p>
                        <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${lookupResult.container_info.fill_percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{lookupResult.container_info.fill_percentage}% Full</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-primary mt-4">
                    Create an account to see full order history and updates!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            {forgotSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Check Your Email</h3>
                <p className="text-gray-600 mb-6">
                  We've sent a password reset link to <strong>{forgotEmail}</strong>. 
                  Click the link in the email to set a new password.
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSent(false);
                  }}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">Forgot Password?</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Address</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      placeholder="your.email@example.com"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-primary hover:bg-primary/90"
                      disabled={forgotLoading}
                      data-testid="forgot-submit"
                    >
                      {forgotLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Balance Payment Modal with Square */}
      {balancePaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Pay Balance</h2>
                <button 
                  onClick={() => {
                    setBalancePaymentModal(null);
                    setBalanceOtpSent(false);
                    setBalanceOtpCode('');
                    setBalanceOtpVerified(false);
                    setBalanceVerificationToken(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="font-semibold text-gray-900 mb-2">{balancePaymentModal.product_name}</p>
                <p className="text-sm text-gray-600 mb-3">{balancePaymentModal.sqft?.toLocaleString()} sqft</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance Amount:</span>
                    <span>${balancePaymentModal.balance_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  {balancePaymentModal.balance_tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span>${balancePaymentModal.balance_tax?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {balancePaymentModal.service_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Fee (3%):</span>
                      <span>${balancePaymentModal.service_fee?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t mt-2">
                    <span>Total Due:</span>
                    <span className="text-primary">${balancePaymentModal.total_due?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* OTP Verification */}
              {!balanceOtpVerified ? (
                <div className="mb-6">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-sm font-medium text-blue-900 mb-3">
                      Verify your email to continue
                    </p>
                    <p className="text-xs text-blue-700 mb-4">
                      We'll send a 6-digit code to <strong>{balancePaymentModal.email}</strong>
                    </p>
                    
                    {!balanceOtpSent ? (
                      <Button
                        onClick={handleSendBalanceOtp}
                        disabled={sendingBalanceOtp}
                        className="w-full"
                      >
                        {sendingBalanceOtp ? 'Sending...' : 'Send Verification Code'}
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="Enter 6-digit code"
                          value={balanceOtpCode}
                          onChange={(e) => setBalanceOtpCode(e.target.value.replace(/\D/g, ''))}
                          className="w-full p-3 border-2 border-gray-200 rounded-lg text-center text-2xl tracking-widest font-mono"
                        />
                        <Button
                          onClick={handleVerifyBalanceOtp}
                          disabled={verifyingBalanceOtp || balanceOtpCode.length !== 6}
                          className="w-full"
                        >
                          {verifyingBalanceOtp ? 'Verifying...' : 'Verify Code'}
                        </Button>
                        <button
                          onClick={handleSendBalanceOtp}
                          disabled={sendingBalanceOtp}
                          className="text-sm text-blue-600 hover:underline w-full text-center"
                        >
                          Resend code
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <SquarePaymentForm
                  amount={balancePaymentModal.total_due}
                  email={balancePaymentModal.email}
                  verificationToken={balanceVerificationToken}
                  orderData={{
                    pledge_id: balancePaymentModal.pledge_id,
                    product_name: balancePaymentModal.product_name,
                    payment_type: 'balance'
                  }}
                  onSuccess={handleBalancePaymentSuccess}
                  onError={(error) => toast.error(error)}
                  disabled={false}
                  forceDirectCharge={true}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default MyOrders;
